import type { CombatState, CombatEntity as Entity } from '../types/interfaces';

// ─────────────────────────────────────────────────────────────────────────────
// Espelho de backend/src/Service/WeatherRules.php — o preview de alcance e o
// que fica escondido na neblina precisam bater com o que o servidor aceita.
// Só vale quando o combate tem weather.effects (missões); senão 'NONE'.
// ─────────────────────────────────────────────────────────────────────────────

export const FOG_RANGE_PENALTY = 2;
export const FOG_VISION = 4;

export type EffectiveWeather = string; // tipo do clima, ou 'NONE' (só visual)

export function effectiveWeather(combat: Pick<CombatState, 'map' | 'weather'>): EffectiveWeather {
  return combat.weather?.effects ? (combat.map.weather_type ?? 'SUNNY') : 'NONE';
}

/** Alcance máximo com neblina: só feitiços à distância encolhem, nunca abaixo do mínimo. */
export function fogRangeMax(rangeMin: number, rangeMax: number, weather: EffectiveWeather): number {
  if (weather !== 'FOG' || rangeMax <= 1) return rangeMax;
  return Math.max(Math.max(1, rangeMin), rangeMax - FOG_RANGE_PENALTY);
}

export function hiddenByFog(x: number, y: number, observers: Array<{ x: number; y: number }>, weather: EffectiveWeather): boolean {
  if (weather !== 'FOG') return false;
  return !observers.some(o => Math.abs(x - o.x) + Math.abs(y - o.y) <= FOG_VISION);
}

/** Aliados vivos de `team` — quem enxerga através da neblina. */
export function fogObservers(entities: Record<string, Entity>, team: string): Array<{ x: number; y: number }> {
  return Object.values(entities)
    .filter(e => e.team === team && e.hp > 0)
    .map(e => ({ x: e.position_x, y: e.position_y }));
}

/** Inimigos que o time `myTeam` não enxerga na neblina (não são desenhados). */
export function fogHiddenIds(entities: Record<string, Entity>, myTeam: string, weather: EffectiveWeather): Set<string> {
  const hidden = new Set<string>();
  if (weather !== 'FOG') return hidden;
  const obs = fogObservers(entities, myTeam);
  for (const e of Object.values(entities)) {
    if (e.team !== myTeam && hiddenByFog(e.position_x, e.position_y, obs, weather)) hidden.add(e.id);
  }
  return hidden;
}

/** Chave de tradução do aviso do clima ao entrar no nível (null = sem efeito). */
export function weatherNotice(weather: EffectiveWeather): string | null {
  return weather === 'RAIN' || weather === 'FOG' || weather === 'STORM' ? `weather.notice.${weather}` : null;
}
